import { defineComponent as _defineComponent } from 'vue'
import Job, { ACTIVE, CANCELLED, UNCANCELLED, COMPLETED } from '@/models/job'
import { useStore } from 'vuex-composition-helpers'
import { computed } from 'vue'
import Activity from '@/models/activity'
import VsMenu from '@/components/vision/VsMenu.vue'
import VsButton from '@/components/vision/VsButton.vue'
import { User as UserAuth0 } from '@auth0/auth0-spa-js/dist/typings/global'
import { getInstance } from '@/auth'
import { actionTypes } from '@/components/tickets/ActionTypes'
import { useJobBottomSheetStatesStore } from '@/components/jobs/jobBottomSheetStatesStore'


export default /*#__PURE__*/_defineComponent({
  __name: 'ActionBar',
  props: {
    jobId: null,
    isPdfDownloading: { type: Boolean },
    isSaving: { type: Boolean },
    fullWidth: { type: Boolean }
  },
  emits: ['on-click'],
  setup(__props: any, { emit }) {

const props = __props as {
  jobId: number
  isPdfDownloading?: boolean
  isSaving?: boolean
  fullWidth?: boolean
}



const jobBottomSheetStatesStore = useJobBottomSheetStatesStore()
const auth: { user: UserAuth0 } = getInstance()
const store = useStore()
const getJobById = computed<(jobNumber: number) => Job>(
  () => store.getters['jobs/getById']
)
const getActivityById = computed<(activityId: number) => Activity>(
  () => store.getters['activities/getById']
)
const canSetJobState = computed<(hubId: string) => boolean>(
  () => store.getters['permissions/canSetJobState']
)
const canAssignJob = computed<(hubId: string) => boolean>(
  () => store.getters['permissions/canAssignResourceToJob']
)
const canCancelJob = computed<
  (hubId: string, userIsRequesterOnUnassignedJob: boolean) => boolean
>(() => store.getters['permissions/canCancelJob'])
const canReopenJob = computed<(hubId: string, jobAccountId: string) => boolean>(
  () => store.getters['permissions/canReopenJob']
)
const canArchiveJob = computed<(hubId: string, accountId: string) => boolean>(
  () => store.getters['permissions/canArchiveJob']
)
const canEditBatch = computed<(batchOwnerId: string | null) => boolean>(
  () => store.getters['permissions/canEditBatch']
)
const canViewBatch = computed<(batchOwnerId: string | null) => boolean>(
  () => store.getters['permissions/canViewBatch']
)
const canViewAddToBatchSelectionPage = computed<boolean>(
  () => store.getters['permissions/canViewAddToBatchSelectionPage']
)

const job = computed(() => getJobById.value(props.jobId))

const actions = {
  addToBatch: {
    text: 'Add to batch',
    icon: 'drive_folder_upload',
    onClick: () => {
      jobBottomSheetStatesStore.setJobBottomSheetStates({
        isBatchBottomSheetVisible: true,
      })
      handleClick({
        type: actionTypes.ADD_TO_BATCH,
      })
    },
    isPdfButton: false,
  },
  addToProject: {
    text: 'Add to project',
    icon: 'drive_folder_upload',
    onClick: () => {
      jobBottomSheetStatesStore.setJobBottomSheetStates({
        isProjectBottomSheetVisible: true,
      })
      handleClick({
        type: actionTypes.ADD_TO_PROJECT,
      })
    },
    isPdfButton: false,
  },
  viewProject: {
    text: 'View project',
    icon: 'folder_open',
    onClick: () =>
      handleClick({
        type: actionTypes.VIEW_PROJECT,
      }),
    isPdfButton: false,
  },
  viewBatch: {
    text: 'View batch',
    icon: 'mdi-wallet-bifold-outline',
    onClick: () =>
      handleClick({
        type: actionTypes.VIEW_BATCH,
      }),
    isPdfButton: false,
  },
  removeFromProject: {
    text: 'Remove from project',
    icon: 'delete_outline',
    onClick: () =>
      handleClick({
        type: actionTypes.REMOVE_FROM_PROJECT,
      }),
    isPdfButton: false,
  },
  removeFromBatch: {
    text: 'Remove from batch',
    icon: 'delete_outline',
    onClick: () =>
      handleClick({
        type: actionTypes.REMOVE_FROM_BATCH,
      }),
    isPdfButton: false,
  },
  sendNotification: {
    text: 'Notify',
    icon: 'send',
    onClick: () =>
      handleClick({
        type: actionTypes.SEND_NOTIFICATION,
        value: true,
      }),
    isPdfButton: false,
  },
  clone: {
    text: 'Clone',
    icon: 'content_copy',
    onClick: () => handleClick({ type: actionTypes.CLONE }),
    isPdfButton: false,
  },
  focusResource: {
    text: 'Assign job to...',
    onClick: () =>
      handleClick({
        type: actionTypes.FOCUS_RESOURCE,
      }),
    isPdfButton: false,
  },
  complete: {
    text: 'Mark as Completed',
    icon: 'mdi-checkbox-marked-outline',
    onClick: () =>
      handleClick({
        type: actionTypes.STATUS_CHANGE,
        value: COMPLETED,
      }),
    isPdfButton: false,
  },
  cancel: {
    text: 'Mark as Cancelled',
    icon: 'highlight_off',
    onClick: () =>
      handleClick({
        type: actionTypes.STATUS_CHANGE,
        value: CANCELLED,
      }),
    isPdfButton: false,
  },
  unCancel: {
    text: 'Reopen',
    icon: 'mdi-backup-restore',
    onClick: () =>
      handleClick({
        type: actionTypes.STATUS_CHANGE,
        value: UNCANCELLED,
      }),
    isPdfButton: false,
  },
  archive: {
    text: 'Archive',
    icon: 'mdi-archive-arrow-down-outline',
    onClick: () =>
      handleClick({
        type: actionTypes.ARCHIVE,
      }),
    isPdfButton: false,
  },
  unarchive: {
    text: 'Unarchive',
    icon: 'mdi-archive-arrow-up-outline',
    onClick: () =>
      handleClick({
        type: actionTypes.UNARCHIVE,
      }),
    isPdfButton: false,
  },
  activate: {
    text: 'Mark as Active',
    onClick: () =>
      handleClick({
        type: actionTypes.STATUS_CHANGE,
        value: ACTIVE,
      }),
    isPdfButton: false,
  },
  downloadPdf: {
    text: 'Download as PDF',
    icon: 'picture_as_pdf',
    onClick: () => handleClick({ type: actionTypes.DOWNLOAD_PDF }),
    isPdfButton: true,
  },
}

const primaryAction = computed(() => {
  if (
    job.value.isArchived &&
    canArchiveJob.value(job.value.hubId, job.value.accountId)
  ) {
    return actions.unarchive
  }

  if (showStatusActions.value) {
    if (job.value.canGoActive) {
      return actions.activate
    }
    return actions.complete
  }

  if (showAssignOption.value) {
    return actions.focusResource
  }

  return actions.sendNotification
})

const secondaryAction = actions.clone

const menuItems = computed(() => {
  const commonActions: {
    text: string
    icon?: string
    onClick: () => void
    isPdfButton?: boolean
  }[] = []
  const stateActions: (
    | {
        text: string
        icon?: string
        onClick: () => void
        isPdfButton?: boolean
      }
    | { divider: boolean }
  )[] = []
  commonActions.push(actions.clone)
  commonActions.push(actions.sendNotification)

  if (!job.value.projectId) {
    commonActions.push(actions.addToProject)
  } else {
    commonActions.push(actions.viewProject)
    commonActions.push(actions.removeFromProject)
  }

  if (
    job.value.invoiceId &&
    canEditBatch.value(job.value.serviceProviderBusinessUnitId)
  ) {
    commonActions.push(actions.removeFromBatch)
  }
  if (!job.value.invoiceId && canViewAddToBatchSelectionPage.value) {
    commonActions.push(actions.addToBatch)
  }
  if (
    job.value.invoiceId &&
    canViewBatch.value(job.value.serviceProviderBusinessUnitId)
  ) {
    commonActions.push(actions.viewBatch)
  }

  if (hasPdfTemplates.value) {
    commonActions.push(actions.downloadPdf)
  }

  if (showCancelAction.value) {
    stateActions.push(actions.cancel)
  }

  if (showUnCancelAction.value) {
    stateActions.push(actions.unCancel)
  }

  if (showArchiveAction.value) {
    stateActions.push(actions.archive)
  }

  if (showUnArchiveAction.value) {
    stateActions.push(actions.unarchive)
  }

  if (showCompleteAction.value) {
    stateActions.push(actions.complete)
    stateActions.push({ divider: true })
  }

  const actions2 = [...stateActions, ...commonActions]
  return actions2.filter((action) => action !== primaryAction.value)
})

const showStatusActions = computed(
  () =>
    !job.value.isArchived &&
    canSetJobState.value(job.value.hubId) &&
    !job.value.awaitingSchedule &&
    !job.value.isCompleted &&
    !job.value.isCancelled
)

const showCancelAction = computed(() => {
  const userIsRequesterOnUnassignedJob =
    job.value.awaitingSchedule && job.value.customerSid === auth.user.sub
  return (
    !job.value.isArchived &&
    canCancelJob.value(job.value.hubId, userIsRequesterOnUnassignedJob) &&
    !job.value.isCancelled
  )
})
const showUnCancelAction = computed(() => {
  return (
    !job.value.isArchived &&
    canReopenJob.value(job.value.hubId, job.value.accountId) &&
    job.value.isCancelled
  )
})
const showArchiveAction = computed(
  () =>
    canArchiveJob.value(job.value.hubId, job.value.accountId) &&
    !job.value.isArchived
)
const showUnArchiveAction = computed(
  () =>
    canArchiveJob.value(job.value.hubId, job.value.accountId) &&
    job.value.isArchived
)
const showCompleteAction = computed(
  () =>
    canSetJobState.value(job.value.hubId) &&
    !job.value.isCancelled &&
    !job.value.isArchived
)
const showAssignOption = computed(
  () =>
    job.value.awaitingSchedule &&
    canAssignJob.value(job.value.hubId) &&
    !job.value.isArchived
)

const showOverflowMenu = computed(() => menuItems.value.length > 1)
const hasPdfTemplates = computed(
  () =>
    getActivityById.value(job.value.activityId)?.pdfFormatIds?.length > 0 ??
    false
)



function handleClick(data: unknown) {
  emit('on-click', data)
}

return { __sfc: true,props, jobBottomSheetStatesStore, auth, store, getJobById, getActivityById, canSetJobState, canAssignJob, canCancelJob, canReopenJob, canArchiveJob, canEditBatch, canViewBatch, canViewAddToBatchSelectionPage, job, actions, primaryAction, secondaryAction, menuItems, showStatusActions, showCancelAction, showUnCancelAction, showArchiveAction, showUnArchiveAction, showCompleteAction, showAssignOption, showOverflowMenu, hasPdfTemplates, emit, handleClick, VsMenu, VsButton }
}

})